import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { useMemo } from 'react';
import type { VaultHolder } from '@/lib/procedures/vaultHolders.contract';
import { getParsedValue } from '@/helpers/formatters';
import { usePaginatedOffset } from '@/hooks/usePaginatedOffset';
import type { DistributionAsset, VaultDistributionTableRow } from '../config';
const WAD = 1e18;

/** A holder's fraction of the supply as the WAD `CellAllocation` reads; null when it is unknown. */
const toWadShare = (supplyShare: number | null) => supplyShare === null ? null : BigInt(Math.round(supplyShare * WAD));
interface UseVaultHoldersRowsParams {
  items: readonly VaultHolder[];
  asset: DistributionAsset | null | undefined;
  chainId: ChainId;
  pageIndex: number;
}

/**
 * Turns one `vault.holders` page into table rows. Each row's allocation bar starts where the
 * previous row's ended, across pages, so the bars stack up to the share the listed holders hold.
 */
export const useVaultHoldersRows = t0 => {
  const $ = _c(8);
  const {
    items,
    asset,
    chainId,
    pageIndex
  } = t0;
  const {
    paginatedOffset,
    initOffset,
    setNextOffset
  } = usePaginatedOffset(ZERO);
  let t1;
  bb0: {
    if (!asset) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    initOffset(pageIndex);
    let t2;
    if ($[1] !== asset || $[2] !== chainId || $[3] !== items || $[4] !== pageIndex || $[5] !== paginatedOffset || $[6] !== setNextOffset) {
      const token = parseApiAsset(asset);
      if (items.length === 0) {
        setNextOffset(ZERO, pageIndex);
      }
      t2 = items.map(holder => {
        const share = toWadShare(holder.supplyShare);
        const percentageOffset = paginatedOffset.current;
        setNextOffset(share ?? ZERO, pageIndex);
        return {
          chainId,
          user: {
            address: holder.userAddress
          },
          token,
          assets: BigInt(holder.assets),
          amountUsd: getParsedValue(holder.assetsUsd, safeParseNumber),
          sharePercentage: {
            value: share,
            percentageOffset
          }
        };
      });
      $[1] = asset;
      $[2] = chainId;
      $[3] = items;
      $[4] = pageIndex;
      $[5] = paginatedOffset;
      $[6] = setNextOffset;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = t2;
  }
  return t1;
};